'use client';

import * as SubframeCore from '@subframe/core';
import { FeatherCheck, FeatherLayers } from '@subframe/core';
import React from 'react';
import * as SubframeUtils from '../utils';
import { Badge } from '../common';

interface PlanCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  icon?: React.ReactNode;
  title: string;
  price: string;
  period: string;
  description: string;
  selected?: boolean;
  badge?: {
    text: string;
    variant?: 'success' | 'warning' | 'neutral' | 'brand';
    showDot?: boolean;
  };
  onSelect?: (selected: boolean) => void;
  className?: string;
}

export const PlanCard = React.forwardRef<HTMLDivElement, PlanCardProps>(function PlanCard(
  {
    icon = <FeatherLayers />,
    title,
    price,
    period,
    description,
    selected = false,
    badge,
    onSelect,
    className,
    ...otherProps
  },
  ref,
) {
  const handleClick = () => {
    if (onSelect) {
      onSelect(!selected);
    }
  };

  return (
    <div
      ref={ref}
      className={SubframeUtils.twClassNames(
        'group/plan-card flex w-full max-w-3xl cursor-pointer flex-col items-start overflow-hidden rounded-xl border-2 border-solid bg-white shadow-sm transition-all duration-200 hover:shadow-md',
        selected ? 'border-brand-600' : 'border-neutral-200 hover:border-neutral-300',
        className,
      )}
      onClick={handleClick}
      {...otherProps}
    >
      <div
        className={SubframeUtils.twClassNames(
          'flex w-full items-center gap-1 border-b-2 border-solid px-4 py-2.5',
          selected ? 'border-brand-600' : 'border-neutral-200',
        )}
      >
        <div className="flex flex-1 items-center gap-3">
          <div className="flex items-center justify-center rounded-md border border-solid border-neutral-200 bg-white p-1.5 shadow-xs">
            <SubframeCore.IconWrapper className="text-default-font h-4 w-4">
              {icon}
            </SubframeCore.IconWrapper>
          </div>
          <span className="text-body-bold font-body-bold text-neutral-700">{title}</span>
        </div>

        <div
          className={SubframeUtils.twClassNames(
            'flex items-center justify-center rounded p-0.5 transition-colors',
            selected ? 'bg-brand-600' : 'border border-solid border-neutral-300 bg-white',
          )}
        >
          {selected ? <FeatherCheck className="h-3 w-3 text-white" /> : <div className="h-3 w-3" />}
        </div>
      </div>

      <div className="flex w-full items-start gap-1 px-4 py-4">
        <div className="flex flex-1 flex-col items-start gap-1">
          <div className="flex items-end gap-1">
            <span className="text-[30px] leading-[38px] font-semibold text-neutral-700">
              {price}
            </span>
            <span className="text-caption pb-0.5 text-neutral-600">{period}</span>
          </div>
          <span className="text-caption text-neutral-600">{description}</span>
        </div>

        {badge && (
          <div className="flex items-center gap-1 rounded-md border border-solid border-neutral-200 bg-white px-1.5 py-0.5 shadow-xs">
            {badge.showDot !== false && (
              <div className="flex items-center p-0.5">
                <div
                  className={SubframeUtils.twClassNames(
                    'h-1.5 w-1.5 rounded-full',
                    badge.variant === 'success'
                      ? 'bg-success-500'
                      : badge.variant === 'warning'
                        ? 'bg-warning-500'
                        : badge.variant === 'brand'
                          ? 'bg-brand-500'
                          : 'bg-neutral-400',
                  )}
                />
              </div>
            )}
            <span className="text-[12px] leading-[18px] font-medium text-neutral-700">
              {badge.text}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});
